import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type TargetPlatform, type TargetStatus, type Voice } from '@/api/client'
import { Button, Dialog, ErrorState, Field, FieldGroup, Input, Select, useToast } from '@/ui'
import { buildCredentials, CredentialFields, credentialsComplete, emptyCred } from './Credentials'
import { useInvalidateAccounts, PLATFORM_LABEL } from './shared'
import { emptyVoice, PRESETS, VoiceFields } from './voice'

/** "새 계정": name, platform, status, the voice and the platform's
 *  credentials. Mount it only while open — closing drops what was typed. */
export function NewAccountDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateAccounts()
  const [name, setName] = useState('')
  const [platform, setPlatform] = useState<TargetPlatform>('threads')
  const [status, setStatus] = useState<TargetStatus>('active')
  const [voice, setVoice] = useState<Voice>(emptyVoice)
  const [cred, setCred] = useState(emptyCred)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  const ready = name.trim() !== '' && credentialsComplete(platform, cred)

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      const made = await api.createTarget({
        name: name.trim(),
        platform,
        status,
        credentials: buildCredentials(platform, cred),
        voice,
      })
      await invalidate()
      toast.success(`${made.name} 계정을 만들었다`)
      onClose()
      navigate(`/accounts/${made.id}`)
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="새 계정"
      description="글을 올리는 계정. 자격은 서버에 암호화해 저장되고 화면에는 다시 나오지 않는다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" loading={busy} disabled={!ready} onClick={() => void submit()}>
            저장
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] space-y-5 overflow-y-auto pb-2 pr-1">
        <FieldGroup columns={2}>
          <Field label="이름" required className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: noveldesk.app" />
          </Field>
          <Field label="플랫폼">
            <Select value={platform} onChange={(e) => setPlatform(e.target.value as TargetPlatform)}>
              {(Object.keys(PLATFORM_LABEL) as TargetPlatform[]).map((p) => (
                <option key={p} value={p}>
                  {PLATFORM_LABEL[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="상태">
            <Select value={status} onChange={(e) => setStatus(e.target.value as TargetStatus)}>
              <option value="active">사용</option>
              <option value="disabled">중지</option>
            </Select>
          </Field>
        </FieldGroup>

        <FieldGroup title="자격">
          <CredentialFields platform={platform} value={cred} onChange={setCred} />
        </FieldGroup>

        <FieldGroup title="말투" description="이 계정이 쓰는 모든 글에 적용된다. 나중에 바꿀 수 있다.">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-meta text-muted-foreground">프리셋</span>
            {PRESETS.map((p) => (
              <Button key={p.id} size="sm" title={p.note} onClick={() => setVoice(p.voice)}>
                {p.label}
              </Button>
            ))}
          </div>
          <VoiceFields voice={voice} onChange={setVoice} />
        </FieldGroup>

        {err != null && <ErrorState title="저장하지 못했다" error={err} />}
      </div>
    </Dialog>
  )
}
